import { gql, useMutation, useQuery } from "@apollo/client";
import { useEffect, useState, useMemo } from 'react';


const GET_CONFIG = gql`
  query GetConfig {
    fs_configurationCollection {
      edges {
        node {
          id
          name
          int
          bool
          text
          datetime
        }
      }
    }
  }
`;

const UPDATE_CONFIG = gql`
  mutation UpdateConfig($new_value: fs_configurationUpdateInput!, $id: BigInt!) {
    updatefs_configurationCollection(
      set: $new_value
      filter: { id: { eq: $id } }
    ) {
      affectedCount
    }
  }
`;

const INSERT_CONFIG = gql`
  mutation AddConfig(
    $name: String!
    $str_val: String
    $int_val: Int
    $bool_val: Boolean
    $datetime_val: Datetime
  ) {
    insertIntofs_configurationCollection(
      objects: {
        name: $name
        int: $int_val
        text: $str_val
        bool: $bool_val
        datetime: $datetime_val
      }
    ) {
      affectedCount
      records {
        id
        name
        int
        bool
        text
        datetime
      }
    }
  }
`;

// Define a class to represent configuration parameters
class ConfigurationParameter {
  constructor(
    public id: number,
    public name: string,
    public value: number | string | boolean | null
  ) { }
}

// Define a class to manage the configuration
export class Configuration {
  parameters: ConfigurationParameter[] = [];

  constructor(data: any) {
    if (data && data.fs_configurationCollection) {
      this.parameters = data.fs_configurationCollection.edges.map(({ node }: any) => {
        let value: number | string | boolean | null = null;
        if (node.int !== null && node.int !== undefined) {
          value = node.int;
        } else if (node.text !== null && node.text !== undefined) {
          value = node.text;
        } else if (node.bool !== null && node.bool !== undefined) {
          value = node.bool;
        } else if (node.datetime !== null && node.datetime !== undefined) {
          value = node.datetime;
        }
        return new ConfigurationParameter(node.id, node.name, value);
      });
    }
  }
  
  // Generic method to get parameter by name
  getParameterByName(name: string): ConfigurationParameter | undefined {
    return this.parameters.find((param) => param.name === name);
  }

  // Get string parameter by name
  getStringParameterByName(name: string): string | null {
    const param = this.getParameterByName(name);
    return param && typeof param.value === 'string' ? param.value : null;
  }

  updateParameter(id: number, name: string, value: number | string | boolean | null) {
    const param = this.getParameterByName(name);
    if (param) {
      param.value = value;
    } else {
      const newId = this.parameters.length > 0 ? Math.max(...this.parameters.map(p => p.id)) + 1 : 1;
      this.parameters.push(new ConfigurationParameter(newId, name, value));
    }
  }
}

export function useConfiguration() {
  const { data, loading, error, refetch } = useQuery(GET_CONFIG);
  const [updateConfig] = useMutation(UPDATE_CONFIG);
  const [insertConfig] = useMutation(INSERT_CONFIG);

  const config = useMemo(
    () => (data ? new Configuration(data) : null),
    [data]
  );

  const save = async (name: string, value: number | string | boolean | null) => {
    if (!config) return;

    const param = config.getParameterByName(name);

    const payload =
      typeof value === 'number'
        ? { int: value }
        : typeof value === 'boolean'
        ? { bool: value }
        : { text: value };

    if (param) {
      await updateConfig({ variables: { id: param.id, new_value: payload } });
    } else {
      await insertConfig({
        variables: {
          name,
          ...(typeof value === 'number'
            ? { int_val: value }
            : typeof value === 'boolean'
            ? { bool_val: value }
            : { str_val: value }),
          datetime_val: null,
        },
      });
    }

    await refetch();
  };

  return { config, save, loading, error };
}
